import { MYSTERY_IDS, type ClueSlot, type MysteryId } from './types';

/**
 * Die Beweislage.
 *
 * Sammelt, was der Spieler tatsaechlich geborgen hat, und bewertet, wofuer es
 * spricht. Entscheidend ist, dass hier nur *gesammelte* Hinweise stehen: Wer
 * Missionen verpasst, hat am Ende eine duennere Grundlage und kann sich
 * durchaus irren. Das ist ein erzaehlerischer Ausgang, kein Fehler.
 */

export interface CollectedClue {
  readonly templateId: string;
  readonly mystery: MysteryId;
  readonly slot: ClueSlot;
  readonly textKey: string;
  readonly weight: number;
  /** Beat des Skeletts, aus dem der Hinweis stammt. */
  readonly beatIndex: number;
  readonly act: 1 | 2 | 3;
  /** Woher er kam — fuer die Anzeige in der Beweislage. */
  readonly sourceLabel: string;
  /**
   * Werte fuer den Fundtext. Nur die personalisierte Spur der Vermissten
   * braucht sie — dort steht der Name der eigenen verlorenen Person.
   */
  readonly params?: Readonly<Record<string, string>>;
}

export interface MysteryScore {
  readonly mystery: MysteryId;
  readonly score: number;
  readonly clueCount: number;
}

export class EvidenceLedger {
  private readonly clues: CollectedClue[] = [];

  add(clue: CollectedClue): void {
    // Derselbe Hinweis darf nicht doppelt zaehlen.
    if (this.clues.some((c) => c.templateId === clue.templateId)) return;
    this.clues.push(clue);
  }

  get all(): readonly CollectedClue[] {
    return this.clues;
  }

  get size(): number {
    return this.clues.length;
  }

  has(templateId: string): boolean {
    return this.clues.some((c) => c.templateId === templateId);
  }

  supportFor(mystery: MysteryId): number {
    return this.clues
      .filter((c) => c.mystery === mystery)
      .reduce((sum, c) => sum + c.weight, 0);
  }

  /** Alle Aufloesungen nach Beweisgewicht, absteigend. */
  ranking(): MysteryScore[] {
    return MYSTERY_IDS.map((mystery) => ({
      mystery,
      score: this.supportFor(mystery),
      clueCount: this.clues.filter((c) => c.mystery === mystery).length,
    })).sort((a, b) => b.score - a.score || a.mystery.localeCompare(b.mystery));
  }

  /**
   * Die fuehrenden Aufloesungen. Mehrere, wenn sie gleichauf liegen.
   * `tolerance` erlaubt es, knappe Abstaende noch als unentschieden zu werten.
   */
  leaders(tolerance = 0): MysteryId[] {
    const ranked = this.ranking();
    const top = ranked[0];
    if (top === undefined || top.score === 0) return [];
    return ranked.filter((r) => top.score - r.score <= tolerance).map((r) => r.mystery);
  }

  /** Gibt es eine einzige, eindeutig fuehrende Aufloesung? */
  hasUniqueLeader(tolerance = 0): boolean {
    return this.leaders(tolerance).length === 1;
  }

  /** Eine Teil-Beweislage, etwa nur die weichen Hinweise aus Akt I und II. */
  filtered(predicate: (clue: CollectedClue) => boolean): EvidenceLedger {
    const subset = new EvidenceLedger();
    for (const clue of this.clues) {
      if (predicate(clue)) subset.add(clue);
    }
    return subset;
  }

  /**
   * Bewertet eine Anklage des Spielers.
   *
   * `correct` sagt, ob er die Wahrheit getroffen hat; `supported` sagt, ob
   * seine These die beste war, die sich aus seinen Funden ziehen liess. Beides
   * kann auseinanderfallen — eine gut belegte, aber falsche Schlussfolgerung
   * ist der interessanteste Ausgang.
   */
  judge(accusation: MysteryId, truth: MysteryId): {
    correct: boolean;
    supported: boolean;
    score: number;
    leadingScore: number;
  } {
    const ranked = this.ranking();
    const leading = ranked[0]?.score ?? 0;
    return {
      correct: accusation === truth,
      supported: this.supportFor(accusation) >= leading && leading > 0,
      score: this.supportFor(accusation),
      leadingScore: leading,
    };
  }
}
